import React from 'react';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';
import SkillBar from '../components/SkillBar';
import { Skill } from '../types';
import { useTranslation } from '../contexts/LanguageContext';

const SkillsPage: React.FC = () => {
  const { t } = useTranslation();
  const skillCategories = [
    {
      name: t('skills.category.frontend'),
      description: t('skills.category.frontendDesc'),
      icon: '🎨',
      skills: [
        { id: '1', name: 'React', category: 'frontend', level: 5 },
        { id: '2', name: 'HTML5 & CSS3', category: 'frontend', level: 5 },
        { id: '3', name: 'JavaScript (ES6+)', category: 'frontend', level: 5 },
        { id: '4', name: 'TypeScript', category: 'frontend', level: 4 },
        { id: '5', name: 'Tailwind CSS', category: 'frontend', level: 5 },
        { id: '6', name: 'Redux', category: 'frontend', level: 4 },
        { id: '7', name: 'Next.js', category: 'frontend', level: 4 },
      ],
    },
    {
      name: t('skills.category.backend'),
      description: t('skills.category.backendDesc'),
      icon: '⚙️',
      skills: [
        { id: '8', name: 'Node.js', category: 'backend', level: 4 },
        { id: '9', name: 'Express.js', category: 'backend', level: 4 },
        { id: '10', name: 'MongoDB', category: 'backend', level: 4 },
        { id: '11', name: 'RESTful API Design', category: 'backend', level: 5 },
        { id: '12', name: 'Authentication & Security', category: 'backend', level: 4 },
      ],
    },
    {
      name: t('skills.category.design'),
      description: t('skills.category.designDesc'),
      icon: '🎭',
      skills: [
        { id: '13', name: 'UI/UX Design', category: 'design', level: 4 },
        { id: '14', name: 'Responsive Design', category: 'design', level: 5 },
      ],
    },
    {
      name: t('skills.category.tools'),
      description: t('skills.category.toolsDesc'),
      icon: '🔧',
      skills: [
        { id: '15', name: 'Git & GitHub', category: 'tools', level: 5 },
        { id: '16', name: 'Agile Development', category: 'tools', level: 4 },
        { id: '17', name: 'Testing (Jest, React Testing Library)', category: 'tools', level: 4 },
        { id: '18', name: 'CI/CD', category: 'tools', level: 3 },
      ],
    },
    {
      name: t('skills.category.productivity'),
      description: t('skills.category.productivityDesc'),
      icon: '🧩',
      skills: [
        { id: '19', name: 'Microsoft Word', category: 'Productivity Tools', level: 5 },
        { id: '20', name: 'Microsoft Excel', category: 'Productivity Tools', level: 4 },
        { id: '21', name: 'Microsoft PowerPoint', category: 'Productivity Tools', level: 5 },
      ],
    },
  ];

  return (
    <>
      <SEO
        title={t('skills.seo.title')}
        description={t('skills.seo.description')}
        keywords={['skills', 'expertise', 'web development', 'frontend', 'backend', 'design']}
        ogUrl="/skills"
      />
      
      <div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-12 mt-20"
        >
          <h1 className="text-4xl font-bold text-white mb-4 my-8">
            {t('skills.title')}
          </h1>
          <p className="text-lg text-primary-100 max-w-3xl ">
            {t('skills.description')}
          </p>
        </motion.div>
        
        <div className="space-y-12">
          {skillCategories.map((category, categoryIndex) => (
            <motion.section
              key={category.name}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: categoryIndex * 0.1 }}
              className="bg-opacity-15 bg-sky-800 rounded-xl shadow-md p-6 md:p-8"
            >
              <div className="flex items-center mb-6">
                <span className="text-3xl mr-3" aria-hidden="true">
                  {category.icon}
                </span>
                <div>
                  <h2 className="text-2xl font-bold text-white">{category.name}</h2>
                  <p className="text-primary-100">{category.description}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
                {category.skills.map((skill, index) => (
                  <SkillBar key={skill.id} skill={skill as Skill} index={index} />
                ))}
              </div>
            </motion.section>
          ))}
        </div>
        
        {/* Learning & Goals Section */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mt-12 bg-gradient-to-r from-sky-300 to-sky-700 rounded-xl shadow-md p-6 md:p-8"
        >
          <h2 className="text-2xl font-bold text-white mb-4">
            {t('skills.learning.title')}
          </h2>
          <p className="text-primary-200 mb-6">
            {t('skills.learning.intro')}
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-opacity-15 bg-sky-800 rounded-lg p-5 shadow-sm">
              <h3 className="text-lg font-semibold text-white mb-2">
                {t('skills.learning.web3.title')}
              </h3>
              <p className="text-primary-200">
                {t('skills.learning.web3.text')}
              </p>
            </div>
            
            <div className="bg-opacity-15 bg-sky-800 rounded-lg p-5 shadow-sm">
              <h3 className="text-lg font-semibold text-white mb-2">
                {t('skills.learning.animation.title')}
              </h3>
              <p className="text-primary-200">
                {t('skills.learning.animation.text')}
              </p>
            </div>
            
            <div className="bg-opacity-15 bg-sky-800 rounded-lg p-5 shadow-sm">
              <h3 className="text-lg font-semibold text-white mb-2">
                {t('skills.learning.ai.title')}
              </h3>
              <p className="text-primary-200">
                {t('skills.learning.ai.text')}
              </p>
            </div>
          </div>
        </motion.section>
      </div>
    </>
  );
};

export default SkillsPage;